import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  MAIL_ACCENT_IDS,
  persistMailAccent,
  readMailAccent,
  type MailAccentId,
} from "@/lib/theme/mailAccent";
import {
  applyMailTheme,
  persistMailTheme,
  readMailTheme,
  toggleMailTheme,
  type MailTheme,
} from "@/lib/theme/mailTheme";

type MailThemeContextValue = {
  theme: MailTheme;
  accent: MailAccentId;
  setTheme: (theme: MailTheme) => void;
  setAccent: (accent: MailAccentId) => void;
  toggleTheme: () => void;
};

const MailThemeContext = createContext<MailThemeContextValue | null>(null);

export function MailThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<MailTheme>(() => readMailTheme());
  const [accent, setAccentState] = useState<MailAccentId>(() => readMailAccent());

  useEffect(() => {
    applyMailTheme(theme, accent);
  }, [theme, accent]);

  const setTheme = useCallback((next: MailTheme) => {
    persistMailTheme(next);
    setThemeState(next);
  }, []);

  const setAccent = useCallback((next: MailAccentId) => {
    if (!MAIL_ACCENT_IDS.includes(next)) return;
    persistMailAccent(next);
    setAccentState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = toggleMailTheme(prev);
      persistMailTheme(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, accent, setTheme, setAccent, toggleTheme }),
    [theme, accent, setTheme, setAccent, toggleTheme]
  );

  return <MailThemeContext.Provider value={value}>{children}</MailThemeContext.Provider>;
}

export function useMailTheme(): MailThemeContextValue {
  const ctx = useContext(MailThemeContext);
  if (!ctx) {
    throw new Error("useMailTheme doit être utilisé dans MailThemeProvider");
  }
  return ctx;
}
